import { type ComponentType, useEffect, useState } from 'react';

/**
 * HOC, откладывающий монтирование компонента до первого idle-периода браузера (requestIdleCallback).
 *
 * @kind helper
 *
 * Используется для компонентов, которые не нужны при первой отрисовке страницы.
 * Убирает их из критического пути рендера, не блокируя main thread.
 *
 * @typeParam P - пропсы оборачиваемого компонента
 * @returns компонент-обёртка, рендерящий Fallback до idle и WrappedComponent после
 */
export function withDeferred<P extends object>(
    WrappedComponent: ComponentType<P>,
    Fallback?: ComponentType<P>
): ComponentType<P> {
    return function WithDeferred(props: P) {
        const [shouldMount, setShouldMount] = useState(false);

        useEffect(() => {
            if ('requestIdleCallback' in window) {
                const id = requestIdleCallback(() => setShouldMount(true));

                return () => cancelIdleCallback(id);
            }

            const id = setTimeout(() => setShouldMount(true), 0);

            return () => clearTimeout(id);
        }, []);

        if (!shouldMount) {
            return Fallback ? <Fallback {...props} /> : null;
        }

        return <WrappedComponent {...props} />;
    };
}
